import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, ShieldCheck, CheckCircle2, Lock,
  User, Calendar, Hash, Building2, MapPin, Sparkles, Info, Phone,
  Trash2, Plus, Minus,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";

// ─────────────────────────────────────────────────────────────────────────────
// PREVIEW: Unified 4-step Registration Wizard (Buyer + Seller)
// Route: /preview/register-flow#buyer | #seller | #compare
// Public, non-destructive — does not call /auth/register, just renders the
// proposed UI for review.
// ─────────────────────────────────────────────────────────────────────────────
const BUSINESS_TYPES = [
  { v: "llc", l: "LLC" },
  { v: "corp", l: "C-Corp" },
  { v: "s_corp", l: "S-Corp" },
  { v: "sole_prop", l: "Sole Proprietor" },
  { v: "partnership", l: "Partnership" },
  { v: "nonprofit", l: "Nonprofit" },
];
const COUNTRIES = ["US", "CA", "GB", "AU", "DE", "FR", "MX", "JP", "SG", "IN"];
const US_STATES = "AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC".split(" ");
const BUYER_CATS = [
  "Apparel & Accessories", "Beauty & Personal Care", "Food & Beverage",
  "Home & Kitchen", "Electronics", "Health & Wellness", "Toys & Games",
  "Pet Products", "Sports & Outdoors", "Office & School",
];
const SELLER_CATS = [
  "Beauty & Personal Care", "Food & Beverage", "Apparel & Accessories",
  "Home & Kitchen", "Health & Wellness", "Baby & Kids", "Pet Supplies",
  "Office & Stationery", "Electronics & Tech", "Outdoor & Sport",
];
const GMV_BANDS = [
  { v: "<100k",   l: "Under $100K" },
  { v: "100k-1m", l: "$100K – $1M" },
  { v: "1m-10m",  l: "$1M – $10M" },
  { v: "10m+",    l: "$10M+" },
];

export default function RegisterFlowPreview() {
  const loc = useLocation();
  const nav = useNavigate();
  const valid = ["buyer", "seller", "compare"];
  const initial = valid.includes((loc.hash || "").replace("#", ""))
    ? (loc.hash || "").replace("#", "")
    : "buyer";
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    const h = (loc.hash || "").replace("#", "");
    if (valid.includes(h) && h !== tab) setTab(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.hash]);

  const switchTab = (k) => {
    setTab(k);
    nav(`/preview/register-flow#${k}`);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-8 py-10">
          <div className="text-[10px] mono uppercase tracking-[0.25em] text-foreground/45">
            PROPOSAL PREVIEW · NOT YET LIVE
          </div>
          <h1 className="display text-4xl sm:text-5xl mt-2 tracking-[-0.02em]">
            Unified 4-step registration
          </h1>
          <p className="text-foreground/60 mt-3 max-w-2xl text-[15px] leading-relaxed">
            Buyer drops from 7 steps to 4. Seller stays at 4 with address added (KYB-complete).
            <span className="font-medium text-foreground"> Zero duplicate questions.</span>{" "}
            One source of truth that feeds KYB, KYC, the AI underwriter, and the seller brand profile.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 text-[11px] mono uppercase tracking-widest">
            <Pill tone="emerald">Buyer 7 → 4 steps (–43%)</Pill>
            <Pill>Seller 4 steps</Pill>
            <Pill tone="emerald">Mobile phone required</Pill>
            <Pill tone="emerald">Smart "I am the legal owner" toggle</Pill>
            <Pill tone="amber">Kills /buyer/onboarding</Pill>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-8 mt-10">
        <div className="inline-flex gap-1 bg-secondary p-1 rounded-full border border-border">
          {[
            { k: "buyer",   l: "Buyer flow" },
            { k: "seller",  l: "Seller flow" },
            { k: "compare", l: "Before vs after" },
          ].map(t => (
            <button key={t.k} onClick={() => switchTab(t.k)} data-testid={`tab-${t.k}`}
              className={`text-sm rounded-full px-5 py-2 transition-all ${
                tab === t.k ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"
              }`}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-6xl mx-auto px-8 py-10">
        {tab === "buyer"   && <Wizard role="buyer"/>}
        {tab === "seller"  && <Wizard role="seller"/>}
        {tab === "compare" && <Compare/>}
      </div>

      <div className="border-t border-border mt-10">
        <div className="max-w-6xl mx-auto px-8 py-8 flex items-center justify-between flex-wrap gap-4">
          <div className="text-[12.5px] text-foreground/60 max-w-xl">
            Approve this and I'll wire it: rebuild <code>Register.jsx</code> with these 4 steps,
            extend <code>/auth/register</code> to accept the merged payload, retire
            <code> /buyer/onboarding</code>, and migrate existing buyer-onboarding API routes.
          </div>
          <Link to="/" className="text-sm text-foreground/55 hover:text-foreground self-center">
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}

// ─── Interactive 4-step wizard mockup ───────────────────────────────────────
function Wizard({ role }) {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "", password: "",
    legal_name: "", ein: "", business_type: "", country: "US",
    addr1: "", addr2: "", city: "", state: "", zip: "",
    owner_name: "", owner_dob: "", owner_ssn4: "", consent: false,
    is_owner: true, owner_phone: "",  // Option C — toggle + conditional phone
    // step 4 — buyer or seller categories share the same shape
    cats: [], soft_check: false, terms: false,
    // seller-only step 4
    trade_name: "", year_founded: "", gmv: "", fulfillment_country: "US",
  });
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const labels = role === "buyer"
    ? ["You", "Business", "Owner KYC", "Categories"]
    : ["You", "Business", "Owner KYC", "Brand"];

  return (
    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6" data-testid={`wizard-${role}`}>
      <div className="rounded-3xl bg-white border border-border shadow-sm p-7">
        <Stepper step={step} labels={labels}/>

        {step === 1 && <Step1 f={f} set={set}/>}
        {step === 2 && <Step2 f={f} set={set}/>}
        {step === 3 && <Step3 f={f} set={set}/>}
        {step === 4 && (role === "buyer" ? <Step4Buyer f={f} set={set}/> : <Step4Seller f={f} set={set}/>)}

        {/* Nav */}
        <div className="mt-7 flex items-center justify-between gap-3 pt-4 border-t border-border">
          <Button variant="ghost" disabled={step === 1} onClick={() => setStep(s => s - 1)}
            data-testid="prev-btn"
            className="rounded-full h-10 px-4 disabled:opacity-30">
            <ArrowLeft className="w-4 h-4 mr-1.5"/>Back
          </Button>
          {step < 4 ? (
            <Button onClick={() => setStep(s => s + 1)}
              data-testid="next-btn"
              className="rounded-full h-10 px-5 bg-foreground text-background hover:bg-foreground/90">
              Continue <ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
          ) : (
            <Button data-testid="finish-btn"
              className="rounded-full h-10 px-5 bg-foreground text-background hover:bg-foreground/90">
              {role === "buyer" ? "Get instant credit" : "Open seller account"}
              <ArrowRight className="w-4 h-4 ml-1.5"/>
            </Button>
          )}
        </div>
      </div>

      {/* Right rail — what each step solves */}
      <SidePanel role={role} step={step}/>
    </div>
  );
}

// ─── Step 1 — You ───────────────────────────────────────────────────────────
function Step1({ f, set }) {
  return (
    <Section
      label="Step 1"
      title="Your account"
      subtitle="Just the basics — this is who logs into TRAQO."
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <Input value={f.first_name} onChange={e => set("first_name", e.target.value)} className="h-11"/>
        </Field>
        <Field label="Last name">
          <Input value={f.last_name} onChange={e => set("last_name", e.target.value)} className="h-11"/>
        </Field>
      </div>
      <Field label="Work email">
        <Input type="email" value={f.email} onChange={e => set("email", e.target.value)}
          placeholder="you@company.com" className="h-11"/>
      </Field>
      <Field label="Mobile phone *" icon={Phone} hint="Used for account verification, fraud alerts, and admin contact">
        <Input type="tel" value={f.phone} onChange={e => set("phone", e.target.value)}
          placeholder="+1 (415) 555-2671" className="h-11" autoComplete="tel"/>
      </Field>
      <Field label="Password" hint="At least 8 characters">
        <Input type="password" value={f.password} onChange={e => set("password", e.target.value)} className="h-11"/>
      </Field>
    </Section>
  );
}

// ─── Step 2 — Business (KYB) ────────────────────────────────────────────────
function Step2({ f, set }) {
  return (
    <Section
      label="Step 2"
      title="Your business"
      subtitle="The legal entity. We verify these against public records."
      icon={Building2}
    >
      <Field label="Legal business name *">
        <Input value={f.legal_name} onChange={e => set("legal_name", e.target.value)}
          placeholder="ACME Wholesale LLC" className="h-11"/>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="EIN (Federal Tax ID) *" hint="Format 12-3456789">
          <Input value={f.ein} onChange={e => set("ein", e.target.value)} maxLength={10}
            placeholder="12-3456789" className="h-11 mono"/>
        </Field>
        <Field label="Business type *">
          <select value={f.business_type} onChange={e => set("business_type", e.target.value)}
            className="h-11 w-full rounded-md border border-border bg-white px-3 text-sm">
            <option value="">Pick one…</option>
            {BUSINESS_TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Country *">
        <select value={f.country} onChange={e => set("country", e.target.value)}
          className="h-11 w-full rounded-md border border-border bg-white px-3 text-sm">
          {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>

      {/* Address sub-block */}
      <div className="mt-2 rounded-xl bg-secondary/40 border border-border p-4 space-y-3">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55 inline-flex items-center gap-1.5">
          <MapPin className="w-3 h-3"/> Business address
        </div>
        <Field label="Street *">
          <Input value={f.addr1} onChange={e => set("addr1", e.target.value)} placeholder="123 Main St" className="h-10 bg-white"/>
        </Field>
        <Field label="Suite / Unit (optional)">
          <Input value={f.addr2} onChange={e => set("addr2", e.target.value)} className="h-10 bg-white"/>
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="City *">
            <Input value={f.city} onChange={e => set("city", e.target.value)} className="h-10 bg-white"/>
          </Field>
          <Field label="State *">
            <select value={f.state} onChange={e => set("state", e.target.value)}
              className="h-10 w-full rounded-md border border-border bg-white px-3 text-sm">
              <option value="">…</option>
              {US_STATES.map(s => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="ZIP *">
            <Input value={f.zip} onChange={e => set("zip", e.target.value)} maxLength={10} className="h-10 bg-white"/>
          </Field>
        </div>
      </div>
    </Section>
  );
}

// ─── Step 3 — Owner KYC ─────────────────────────────────────────────────────
function Step3({ f, set }) {
  return (
    <Section
      label="Step 3"
      title="Verify the legal owner"
      subtitle="One-time KYC required by our banking partner before the wallet activates."
      icon={ShieldCheck}
    >
      <div className="rounded-xl bg-secondary/40 border border-border p-3.5 flex items-start gap-3">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-foreground/70"/>
        <div className="text-[12.5px] text-foreground/70 leading-relaxed">
          We use this only to confirm identity — <strong>we never store the full SSN</strong>,
          only the last 4 digits, encrypted at rest.
        </div>
      </div>

      {/* Smart toggle — covers the ~30% of signups where the person filling
          out the form isn't the legal owner (e.g. an assistant or CFO signing
          up on behalf of the CEO). When unchecked we reveal an owner-phone
          field so the bank partner has a direct line to the beneficial owner. */}
      <label className="flex items-start gap-2.5 cursor-pointer rounded-xl border border-border bg-white p-3.5 hover:border-foreground/30 transition-colors"
        data-testid="kyc-is-owner-toggle">
        <Checkbox checked={f.is_owner} onCheckedChange={v => set("is_owner", !!v)}/>
        <div>
          <div className="text-[13px] font-medium text-foreground">
            I am the legal owner of this business
          </div>
          <div className="text-[11.5px] text-foreground/55 leading-relaxed mt-0.5">
            Uncheck if you're signing up on behalf of someone else (e.g. CFO, assistant).
            We'll then ask for the owner's phone so our banking partner can verify them directly.
          </div>
        </div>
      </label>

      <Field label="Legal owner full name *" icon={User} hint="As it appears on government ID">
        <Input value={f.owner_name} onChange={e => set("owner_name", e.target.value)}
          placeholder="Jane A. Smith" className="h-11"/>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Date of birth *" icon={Calendar} hint="Must be 18+">
          <Input type="date" value={f.owner_dob} onChange={e => set("owner_dob", e.target.value)}
            max={new Date().toISOString().slice(0, 10)} className="h-11 mono"/>
        </Field>
        <Field label="Last 4 of SSN *" icon={Hash} hint="Last 4 digits only">
          <Input value={f.owner_ssn4}
            onChange={e => set("owner_ssn4", e.target.value.replace(/\D/g, "").slice(0, 4))}
            placeholder="••••" inputMode="numeric" maxLength={4}
            className="h-11 mono tracking-[0.4em]"/>
        </Field>
      </div>

      {/* Conditional: owner phone only if signup user ≠ legal owner */}
      {!f.is_owner && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3 animate-in fade-in slide-in-from-top-1"
          data-testid="kyc-owner-phone-block">
          <div className="text-[11px] mono uppercase tracking-[0.18em] text-amber-800 inline-flex items-center gap-1.5">
            <Phone className="w-3 h-3"/> Owner contact required
          </div>
          <Field label="Legal owner's mobile phone *" icon={Phone}
            hint="We'll reach the owner here for KYC verification — not your phone from Step 1.">
            <Input type="tel" value={f.owner_phone} onChange={e => set("owner_phone", e.target.value)}
              placeholder="+1 (415) 555-2671" className="h-11 bg-white" autoComplete="tel"/>
          </Field>
        </div>
      )}

      <label className="flex items-start gap-2.5 cursor-pointer pt-1">
        <Checkbox checked={f.consent} onCheckedChange={v => set("consent", !!v)}/>
        <span className="text-[12.5px] leading-relaxed text-foreground/75">
          {f.is_owner
            ? <>I am the beneficial owner and authorize TRAQO and its banking partners to verify my identity using the information above.</>
            : <>I am authorized to act on behalf of the legal owner and authorize TRAQO and its banking partners to verify their identity using the information above.</>}
        </span>
      </label>
      <p className="text-[11px] text-foreground/50 leading-relaxed inline-flex items-start gap-1.5">
        <Lock className="w-3 h-3 mt-0.5 shrink-0"/>
        Bank-grade encryption · last-4 only · soft check, no hard credit pull.
      </p>
    </Section>
  );
}

// ─── Step 4 — Buyer-specific ────────────────────────────────────────────────
function Step4Buyer({ f, set }) {
  const toggleCat = (c) => set("cats",
    f.cats.includes(c)
      ? f.cats.filter(x => x !== c)
      : (f.cats.length >= 3 ? f.cats : [...f.cats, c])
  );
  return (
    <Section
      label="Step 4"
      title="What do you buy?"
      subtitle="Pick up to 3 categories that best describe what you stock. Helps us match you with the right suppliers and price your credit line."
      icon={Sparkles}
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {BUYER_CATS.map(c => {
          const sel = f.cats.includes(c);
          return (
            <button key={c} type="button" onClick={() => toggleCat(c)}
              className={`text-left text-[12.5px] rounded-lg border px-3 py-2.5 transition-all ${
                sel
                  ? "border-foreground bg-foreground text-background"
                  : "border-border bg-white hover:border-foreground/40"
              }`}>
              <div className="flex items-center gap-2">
                {sel && <CheckCircle2 className="w-3.5 h-3.5"/>} {c}
              </div>
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-foreground/55">{f.cats.length}/3 selected</div>

      <div className="rounded-xl bg-secondary/40 border border-border p-4 space-y-3 mt-3">
        <div className="display text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4"/> Authorization
        </div>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox checked={f.soft_check} onCheckedChange={v => set("soft_check", !!v)}/>
          <span className="text-[12.5px] leading-relaxed">
            I authorize TRAQO to run a <strong>soft business credit check</strong> using the information above.
            This will not affect any personal credit score.
          </span>
        </label>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox checked={f.terms} onCheckedChange={v => set("terms", !!v)}/>
          <span className="text-[12.5px] leading-relaxed">
            I agree to TRAQO's <a href="#" className="underline">Terms</a>,
            <a href="#" className="underline"> Privacy Policy</a>, and
            <a href="/legal/insurance" target="_blank" rel="noreferrer" className="underline"> Trade Credit Insurance Authorisation</a>.
          </span>
        </label>
      </div>
    </Section>
  );
}

// ─── Step 4 — Seller-specific ───────────────────────────────────────────────
function Step4Seller({ f, set }) {
  const toggleCat = (c) => set("cats",
    f.cats.includes(c)
      ? f.cats.filter(x => x !== c)
      : (f.cats.length >= 3 ? f.cats : [...f.cats, c])
  );
  return (
    <Section
      label="Step 4"
      title="Tell buyers about your brand"
      subtitle="What you sell, where you ship from. This is what buyers see on your public profile."
      icon={Sparkles}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Trade name (DBA)" hint="If different from legal name">
          <Input value={f.trade_name} onChange={e => set("trade_name", e.target.value)}
            placeholder="Glow Cosmetics" className="h-11"/>
        </Field>
        <Field label="Year founded">
          <Input type="number" min={1800} max={new Date().getFullYear()}
            value={f.year_founded} onChange={e => set("year_founded", e.target.value)}
            placeholder="2018" className="h-11 mono"/>
        </Field>
      </div>

      {/* Visual category picker — parity with the buyer step. Sellers can
          pick up to 3 categories; the first one becomes the primary brand
          category that drives buyer-matching and storefront classification. */}
      <Field label="Brand categories *" hint="Pick up to 3 — the first one is your primary category">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
          {SELLER_CATS.map(c => {
            const sel = f.cats.includes(c);
            return (
              <button key={c} type="button" onClick={() => toggleCat(c)}
                className={`text-left text-[12.5px] rounded-lg border px-3 py-2.5 transition-all ${
                  sel
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-white hover:border-foreground/40"
                }`}>
                <div className="flex items-center gap-2">
                  {sel && <CheckCircle2 className="w-3.5 h-3.5"/>} {c}
                </div>
              </button>
            );
          })}
        </div>
        <div className="text-[11px] text-foreground/55 mt-1.5">{f.cats.length}/3 selected</div>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Annual wholesale GMV *" hint="Right-sizes your account tier and fee schedule">
          <select value={f.gmv} onChange={e => set("gmv", e.target.value)}
            className="h-11 w-full rounded-md border border-border bg-white px-3 text-sm">
            <option value="">Pick a range…</option>
            {GMV_BANDS.map(g => <option key={g.v} value={g.v}>{g.l}</option>)}
          </select>
        </Field>
        <Field label="Fulfillment country *" hint="Where your warehouse ships from">
          <select value={f.fulfillment_country} onChange={e => set("fulfillment_country", e.target.value)}
            className="h-11 w-full rounded-md border border-border bg-white px-3 text-sm">
            {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>

      {/* Seller authorization — same standard as buyer. We underwrite seller
          risk too: ability to fulfill, return obligations, AML, fraud. */}
      <div className="rounded-xl bg-secondary/40 border border-border p-4 space-y-3 mt-3">
        <div className="display text-base flex items-center gap-2">
          <ShieldCheck className="w-4 h-4"/> Authorization
        </div>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox checked={f.soft_check} onCheckedChange={v => set("soft_check", !!v)}/>
          <span className="text-[12.5px] leading-relaxed">
            I authorize TRAQO to run a <strong>soft business credit &amp; identity check</strong> on the company.
            We use this to underwrite seller risk (fulfilment ability, return guarantees, AML). Soft pull only — no impact on personal credit.
          </span>
        </label>
        <label className="flex items-start gap-2.5 cursor-pointer">
          <Checkbox checked={f.terms} onCheckedChange={v => set("terms", !!v)}/>
          <span className="text-[12.5px] leading-relaxed">
            I agree to TRAQO's <a href="#" className="underline">Terms</a> and
            <a href="#" className="underline"> Privacy Policy</a>, and confirm I'm authorized to publish this brand on TRAQO.
          </span>
        </label>
      </div>
    </Section>
  );
}

// ─── Side panel that explains the value of each step ───────────────────────
function SidePanel({ role, step }) {
  const content = {
    1: {
      title: "Your account",
      points: [
        "Just identity + login — no business details yet.",
        "Mobile phone is required so admin can reach you for compliance, fraud alerts, or account recovery.",
        "Pre-fills from email-invite links if you got one.",
      ],
    },
    2: {
      title: "KYB — verify the business",
      points: [
        "Legal name + EIN + address feed our AI underwriter and the Allianz Trade insurance check.",
        "Address replaces the old `/buyer/onboarding` step — collected once, used everywhere.",
        "EIN + state combo verified against Secretary of State records.",
      ],
    },
    3: {
      title: "KYC — required by our bank partner",
      points: [
        "USA PATRIOT Act §326: legal-owner identity must be verified before the wallet activates.",
        "Last 4 of SSN only — never the full number. Stored bcrypt-hashed.",
        "Smart toggle: \"I am the legal owner\" reuses your Step 1 phone. Uncheck it (e.g. CFO signing up for the CEO) and we'll ask for the owner's phone instead.",
        "Soft check, no hard credit pull. Won't impact personal FICO.",
      ],
    },
    4: role === "buyer"
      ? {
          title: "Categories + soft-check authorization",
          points: [
            "Categories help us match you with the right suppliers on the platform.",
            "The soft credit check unlocks Net-30/60/90 terms instantly — soft pull only, no FICO impact.",
            "Trade Credit Insurance Authorisation completes the buyer credit-eligibility loop.",
          ],
        }
      : {
          title: "Brand profile + soft-check authorization",
          points: [
            "Pick up to 3 brand categories — they render on your public storefront and drive buyer matching.",
            "Annual GMV right-sizes your account tier and fee schedule.",
            "Soft credit & identity check underwrites seller risk: fulfilment ability, return guarantees, AML.",
            "Fulfillment country drives shipping rate quotes for your buyers.",
          ],
        },
  }[step];

  return (
    <div className="rounded-3xl bg-foreground text-background p-7 relative overflow-hidden">
      <div className="text-[10px] mono uppercase tracking-[0.25em] text-accent">
        Why we ask · step {step} of 4
      </div>
      <h3 className="display text-3xl mt-2 leading-[1.1] tracking-[-0.02em]">{content.title}</h3>
      <ul className="mt-7 space-y-4 text-[13.5px]">
        {content.points.map(p => (
          <li key={p} className="flex gap-3">
            <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5"/>
            <span className="text-background/80 leading-relaxed">{p}</span>
          </li>
        ))}
      </ul>

      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-accent/15 blur-3xl"/>
    </div>
  );
}

// ─── Before vs After comparison ─────────────────────────────────────────────
function Compare() {
  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-5">
        {/* Buyer column */}
        <Column
          title="Buyer signup"
          subtitle="From 7 steps to 4. -43% friction."
          before={[
            ["1. /register · You",       ["First name", "Last name", "Email"]],
            ["2. /register · Business",  ["Company", "EIN", "Password"]],
            ["3. /register · Owner KYC", ["Legal owner name", "DOB", "SSN-last-4", "Consent"]],
            ["4. /onboarding · Business 🔴", ["Legal business name (DUP)", "Trade name", "EIN (DUP)", "Country", "Business type"]],
            ["5. /onboarding · Address", ["Street, City, State, ZIP"]],
            ["6. /onboarding · Contact 🔴", ["Legal owner name (DUP)", "Business email (DUP)", "Phone", "Website"]],
            ["7. /onboarding · Sell", ["Categories", "Soft check", "Terms"]],
          ]}
          after={[
            ["1. You",         ["First name", "Last name", "Email", "Mobile phone", "Password"]],
            ["2. Business",    ["Legal business name", "EIN", "Business type", "Country", "Street, City, State, ZIP"]],
            ["3. Owner KYC",   ["Legal owner name", "DOB", "SSN-last-4", "Consent"]],
            ["4. Categories",  ["Pick 1-3 categories", "Soft credit check authorization", "Terms & TCI"]],
          ]}
        />
        {/* Seller column */}
        <Column
          title="Seller signup"
          subtitle="Stays at 4 steps; address now collected for KYB."
          before={[
            ["1. /register · You",        ["First name", "Last name", "Email"]],
            ["2. /register · Business",   ["Company", "EIN", "Password"]],
            ["3. /register · Owner KYC",  ["Legal owner name", "DOB", "SSN-last-4", "Consent"]],
            ["4. /register · Brand",      ["Trade name", "Year founded", "Country", "Business type", "Fulfillment country", "Annual GMV", "Website", "Brand category"]],
            ["⚠ No phone, no address, no soft-check auth", ["(missing for KYB / contact / underwriting)"]],
          ]}
          after={[
            ["1. You",         ["First name", "Last name", "Email", "Mobile phone", "Password"]],
            ["2. Business",    ["Legal business name", "EIN", "Business type", "Country", "Street, City, State, ZIP"]],
            ["3. Owner KYC",   ["Legal owner name", "DOB", "SSN-last-4", "Consent"]],
            ["4. Brand",       ["Trade name (DBA)", "Year founded", "Pick 1-3 brand categories", "Annual GMV", "Fulfillment country", "Soft credit check authorization", "Terms"]],
          ]}
        />
      </div>

      <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-5 text-[13.5px] text-emerald-900 leading-relaxed">
        <strong>Net effect:</strong>
        <ul className="mt-2 space-y-1.5 list-disc pl-5">
          <li>EIN, company name, owner name, business email — each now collected exactly <strong>once</strong>.</li>
          <li><code>/buyer/onboarding</code> page is retired; its data moves into Steps 2 &amp; 4 of <code>/register</code>.</li>
          <li>Phone &amp; website removed from registration → editable later in <code>Settings → Brand</code> or <code>Settings → Profile</code>.</li>
          <li>Seller now collects address at signup, closing the KYB completeness gap.</li>
          <li>One <code>POST /auth/register</code> payload feeds KYB, KYC, AI underwriter, and the seller brand profile in a single transaction.</li>
        </ul>
      </div>
    </div>
  );
}

function Column({ title, subtitle, before, after }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-6">
      <h3 className="display text-xl tracking-tight">{title}</h3>
      <p className="text-[12.5px] text-foreground/55 mt-1">{subtitle}</p>

      <div className="mt-5">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/45 inline-flex items-center gap-1.5">
          <Trash2 className="w-3 h-3"/> Today
        </div>
        <ol className="mt-2 space-y-2">
          {before.map(([s, fs]) => (
            <li key={s} className="rounded-lg bg-rose-50/60 border border-rose-100 px-3 py-2">
              <div className="text-[12px] font-medium">{s}</div>
              <div className="text-[11px] text-foreground/55 mt-0.5">{fs.join(" · ")}</div>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-5">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-emerald-700 inline-flex items-center gap-1.5">
          <Plus className="w-3 h-3"/> Proposed
        </div>
        <ol className="mt-2 space-y-2">
          {after.map(([s, fs]) => (
            <li key={s} className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2">
              <div className="text-[12px] font-medium">{s}</div>
              <div className="text-[11px] text-foreground/65 mt-0.5">{fs.join(" · ")}</div>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-4 text-[11px] mono uppercase tracking-widest text-foreground/55 inline-flex items-center gap-1.5">
        <Minus className="w-3 h-3"/>
        {title === "Buyer signup" ? "3 steps removed · 4 duplicate fields eliminated" : "Same step count · 5 fields shed (phone, website, dup company, dup EIN, country dup)"}
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function Stepper({ step, labels }) {
  return (
    <div className="flex items-center gap-1.5 text-[10px] mono uppercase tracking-[0.18em]">
      {labels.map((l, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <span key={l} className="flex items-center gap-1.5">
            <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] ${
              done ? "bg-foreground text-background"
                : active ? "bg-accent text-foreground"
                : "bg-secondary text-foreground/45"
            }`}>
              {done ? <CheckCircle2 className="w-3 h-3"/> : n}
            </span>
            <span className={active ? "text-foreground font-semibold" : "text-foreground/45"}>{l}</span>
            {i < labels.length - 1 && <span className="text-foreground/25 mx-1">—</span>}
          </span>
        );
      })}
    </div>
  );
}

function Section({ label, title, subtitle, icon: Icon, children }) {
  return (
    <div className="mt-7 space-y-4">
      <div>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/45">{label}</div>
        <h2 className="display text-2xl tracking-tight mt-1 flex items-center gap-2">
          {Icon && <Icon className="w-5 h-5 text-foreground/65"/>}
          {title}
        </h2>
        {subtitle && <p className="text-[13px] text-foreground/55 mt-1.5 leading-relaxed">{subtitle}</p>}
      </div>
      <div className="space-y-3.5">{children}</div>
    </div>
  );
}

function Field({ label, hint, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-[12.5px] flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/50"/>}
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[11px] text-foreground/50 mt-1">{hint}</div>}
    </div>
  );
}

function Pill({ children, tone = "default" }) {
  const cls = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber:   "bg-amber-50 text-amber-900 border-amber-200",
    default: "bg-secondary text-foreground/70 border-border",
  }[tone];
  return <span className={`chip border ${cls}`}>{children}</span>;
}
